import React, { useState, useRef, useEffect } from 'react';
import {
  Plus,
  Trash2,
  Camera,
  Edit3,
  Check,
  X,
  Package,
  Search,
  Loader2,
  Database,
  MapPin,
  Tag,
  DollarSign,
  Hash,
  FileText,
  Layers,
} from 'lucide-react';
import axios from 'axios';
import { createEmptyItem } from './qconfigurator.types';
import CameraCapture from './CameraCapture';
import LocationPicker from '../LocationPicker';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'https://ventro.energy';

const UNIT_OPTIONS = [
  { value: 'pcs', label: 'Sztuki' },
  { value: 'kg', label: 'Kilogramy' },
  { value: 'm', label: 'Metry' },
  { value: 'm2', label: 'Metry kw.' },
  { value: 'l', label: 'Litry' },
  { value: 'pairs', label: 'Pary' },
  { value: 'sets', label: 'Zestawy' },
  { value: 'rolls', label: 'Rolki' },
  { value: 'boxes', label: 'Pudełka' },
];

const QConfiguratorDrawerInventory = ({
  drawer,
  cabinetName,
  moduleName,
  onUpdateDrawer,
}) => {
  const [isEditingDrawerName, setIsEditingDrawerName] = useState(false);
  const [editedDrawerName, setEditedDrawerName] = useState(drawer.name);
  const drawerNameInputRef = useRef(null);

  const items = drawer.items || [];

  // Warehouse search state
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [warehouseItems, setWarehouseItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [isLoadingWarehouse, setIsLoadingWarehouse] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [availableCategories, setAvailableCategories] = useState([]);
  const [availableLocations, setAvailableLocations] = useState([]);
  const searchInputRef = useRef(null);

  // Item form state (for both add and edit)
  const [isItemFormOpen, setIsItemFormOpen] = useState(false);
  const [editingItemIndex, setEditingItemIndex] = useState(null); // null = adding, number = editing
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [isSavingItem, setIsSavingItem] = useState(false);
  const [isLoadingItemData, setIsLoadingItemData] = useState(false);
  const [itemForm, setItemForm] = useState({
    name: '',
    sku: '',
    category: '',
    subcategory: '',
    description: '',
    unit: 'pcs',
    unit_price: '',
    quantity: 1,
    minimum_stock_level: 0,
    location: '',
    supplier: '',
    warehouseItemId: null,
  });
  const [itemFormImage, setItemFormImage] = useState(null);
  const [showItemFormCamera, setShowItemFormCamera] = useState(false);

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
  }, []);

  // Fetch subcategories when category changes
  useEffect(() => {
    if (itemForm.category) {
      fetchSubcategories(itemForm.category);
    } else {
      setSubcategories([]);
    }
  }, [itemForm.category]);

  // Fetch warehouse items when search opens
  useEffect(() => {
    if (isSearchOpen && warehouseItems.length === 0) {
      fetchWarehouseItems();
    }
    if (isSearchOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isSearchOpen]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/warehouse/categories/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      const data = Array.isArray(response.data) ? response.data : response.data.results || [];
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchSubcategories = async (categoryId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/warehouse/subcategories/?category=${categoryId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      const data = Array.isArray(response.data) ? response.data : response.data.results || [];
      setSubcategories(data);
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      setSubcategories([]);
    }
  };

  // Filter items based on search term and filters
  useEffect(() => {
    const filtered = warehouseItems.filter(item => {
      const matchesSearch = searchTerm === '' ||
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.category && item.category.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.location && item.location.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.sku && item.sku.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
      const matchesLocation = locationFilter === 'all' || item.location === locationFilter;

      return matchesSearch && matchesCategory && matchesLocation;
    });
    setFilteredItems(filtered);
  }, [searchTerm, warehouseItems, categoryFilter, locationFilter]);

  const fetchWarehouseItems = async () => {
    setIsLoadingWarehouse(true);
    setSearchError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/warehouse/items/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      const itemsDataArray = Array.isArray(response.data) ? response.data :
        response.data.results ? response.data.results : [];
      const itemsData = itemsDataArray.map(item => ({
        id: item.id,
        name: item.name,
        category: item.category_name || '',
        subcategory: item.subcategory_name || '',
        availableQty: item.quantity_in_stock,
        unit: item.unit,
        location: item.location || '',
        unitPrice: parseFloat(item.unit_price) || 0,
        sku: item.sku || '',
      }));

      setWarehouseItems(itemsData);
      setFilteredItems(itemsData);

      // Extract unique categories and locations
      const categories = [...new Set(itemsData.map(item => item.category))].filter(Boolean).sort();
      const locations = [...new Set(itemsData.map(item => item.location))].filter(Boolean).sort();

      setAvailableCategories(categories);
      setAvailableLocations(locations);
    } catch (error) {
      console.error('Error fetching warehouse items:', error);
      setSearchError('Failed to load warehouse items. Please try again.');
    } finally {
      setIsLoadingWarehouse(false);
    }
  };

  // Generate unique issue number for cabinet allocations
  const generateIssueNumber = () => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 6).toUpperCase();
    return `CAB-${timestamp}-${random}`;
  };

  // Add warehouse item to drawer (reduces stock and creates allocation)
  const handleAddWarehouseItem = async (warehouseItem) => {
    const quantityToAdd = 1;

    // Check available stock
    if (warehouseItem.availableQty < quantityToAdd) {
      alert('Brak wystarczającej ilości w magazynie');
      return;
    }

    try {
      // 1. Create WarehouseIssue allocation
      const allocationName = `${cabinetName || 'Cabinet'} > ${moduleName || 'Module'} > ${drawer.name}`;
      const issueNumber = generateIssueNumber();

      await axios.post(
        `${API_BASE_URL}/api/warehouse/warehouse-issues/`,
        {
          issue_number: issueNumber,
          issue_type: 'other',
          status: 'issued',
          assigned_to_person: allocationName,
          purpose: `Alokacja do szafki: ${allocationName}`,
          notes: `Przedmiot dodany do konfiguracji szafki`,
          items_data: [
            {
              item_id: warehouseItem.id,
              quantity: quantityToAdd,
              notes: `Szuflada: ${drawer.name}`
            }
          ]
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // 2. Update local warehouse items list (reduce available qty)
      setWarehouseItems(prev => prev.map(item =>
        item.id === warehouseItem.id
          ? { ...item, availableQty: item.availableQty - quantityToAdd }
          : item
      ));

      // 3. Check if item already exists in drawer
      const existingIndex = items.findIndex(i =>
        i.warehouseItemId === warehouseItem.id || i.name === warehouseItem.name
      );

      if (existingIndex !== -1) {
        // Increase quantity of existing item
        onUpdateDrawer({
          ...drawer,
          items: items.map((i, idx) =>
            idx === existingIndex
              ? { ...i, quantity: (i.quantity || 1) + quantityToAdd }
              : i
          ),
        });
      } else {
        // Add new item
        const newItem = {
          ...createEmptyItem(),
          name: warehouseItem.name,
          description: `${warehouseItem.category}${warehouseItem.location ? ' • ' + warehouseItem.location : ''}`,
          quantity: quantityToAdd,
          warehouseItemId: warehouseItem.id,
          unit: warehouseItem.unit,
          sku: warehouseItem.sku,
        };
        onUpdateDrawer({
          ...drawer,
          items: [...items, newItem],
        });
      }
    } catch (error) {
      console.error('Error creating allocation:', error);
      alert('Błąd podczas tworzenia alokacji: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Drawer name editing
  const handleStartEditDrawerName = () => {
    setEditedDrawerName(drawer.name);
    setIsEditingDrawerName(true);
    setTimeout(() => drawerNameInputRef.current?.focus(), 0);
  };

  const handleSaveDrawerName = () => {
    if (editedDrawerName.trim()) {
      onUpdateDrawer({ ...drawer, name: editedDrawerName.trim() });
    }
    setIsEditingDrawerName(false);
  };

  // Reset form to empty state
  const resetItemForm = () => {
    setItemForm({
      name: '',
      sku: '',
      category: '',
      subcategory: '',
      description: '',
      unit: 'pcs',
      unit_price: '',
      quantity: 1,
      minimum_stock_level: 0,
      location: '',
      supplier: '',
      warehouseItemId: null,
    });
    setItemFormImage(null);
    setEditingItemIndex(null);
  };

  // Open add item form
  const handleAddItem = () => {
    resetItemForm();
    setIsItemFormOpen(true);
  };

  // Open edit item form
  const handleEditItem = async (item, index) => {
    setEditingItemIndex(index);
    setItemFormImage(item.image || null);

    // If item has warehouseItemId, fetch full data from warehouse
    if (item.warehouseItemId) {
      setIsLoadingItemData(true);
      setIsItemFormOpen(true);
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/warehouse/items/${item.warehouseItemId}/`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`
            }
          }
        );
        const warehouseItem = response.data;
        setItemForm({
          name: warehouseItem.name || item.name,
          sku: warehouseItem.sku || '',
          category: warehouseItem.category ? String(warehouseItem.category) : '',
          subcategory: warehouseItem.subcategory ? String(warehouseItem.subcategory) : '',
          description: warehouseItem.description || item.description || '',
          unit: warehouseItem.unit || item.unit || 'pcs',
          unit_price: warehouseItem.unit_price || '',
          quantity: item.quantity || 1,
          minimum_stock_level: warehouseItem.minimum_stock_level || 0,
          location: warehouseItem.location || '',
          supplier: warehouseItem.supplier || '',
          warehouseItemId: item.warehouseItemId,
        });
      } catch (error) {
        console.error('Error fetching warehouse item:', error);
        // Fallback to drawer item data
        setItemForm({
          name: item.name || '',
          sku: item.sku || '',
          category: '',
          subcategory: '',
          description: item.description || '',
          unit: item.unit || 'pcs',
          unit_price: '',
          quantity: item.quantity || 1,
          minimum_stock_level: 0,
          location: '',
          supplier: '',
          warehouseItemId: item.warehouseItemId,
        });
      } finally {
        setIsLoadingItemData(false);
      }
    } else {
      // Item without warehouse link - use drawer item data
      setItemForm({
        name: item.name || '',
        sku: item.sku || '',
        category: '',
        subcategory: '',
        description: item.description || '',
        unit: item.unit || 'pcs',
        unit_price: '',
        quantity: item.quantity || 1,
        minimum_stock_level: 0,
        location: '',
        supplier: '',
        warehouseItemId: null,
      });
      setIsItemFormOpen(true);
    }
  };

  // Handle item form change
  const handleItemFormChange = (field, value) => {
    setItemForm(prev => ({ ...prev, [field]: value }));
  };

  // Save item (handles both add and edit)
  const handleSaveItem = async () => {
    if (!itemForm.name.trim()) {
      alert('Nazwa jest wymagana');
      return;
    }

    const isEditing = editingItemIndex !== null;
    const hasWarehouseLink = itemForm.warehouseItemId !== null;

    // For new items, category is required to create warehouse entry
    if (!isEditing && !itemForm.category) {
      alert('Kategoria jest wymagana');
      return;
    }

    setIsSavingItem(true);
    try {
      let warehouseItemId = itemForm.warehouseItemId;
      let categoryName = '';

      // Find category name
      if (itemForm.category) {
        const categoryObj = categories.find(c => c.id === parseInt(itemForm.category));
        categoryName = categoryObj?.name || '';
      }

      // Prepare warehouse data
      const warehouseData = {
        name: itemForm.name,
        sku: itemForm.sku || '',
        category: itemForm.category ? parseInt(itemForm.category) : null,
        subcategory: itemForm.subcategory ? parseInt(itemForm.subcategory) : null,
        description: itemForm.description || '',
        unit: itemForm.unit,
        unit_price: parseFloat(itemForm.unit_price) || 0,
        quantity_in_stock: parseInt(itemForm.quantity) || 1,
        minimum_stock_level: parseInt(itemForm.minimum_stock_level) || 0,
        location: itemForm.location || '',
        supplier: itemForm.supplier || '',
      };

      if (isEditing && hasWarehouseLink) {
        // Update existing warehouse item
        await axios.patch(
          `${API_BASE_URL}/api/warehouse/items/${warehouseItemId}/`,
          warehouseData,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
              'Content-Type': 'application/json'
            }
          }
        );
      } else if (!isEditing && itemForm.category) {
        // Create new warehouse item
        const response = await axios.post(
          `${API_BASE_URL}/api/warehouse/items/`,
          warehouseData,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
              'Content-Type': 'application/json'
            }
          }
        );
        warehouseItemId = response.data.id;

        // Create allocation for the new item
        const allocationName = `${cabinetName || 'Cabinet'} > ${moduleName || 'Module'} > ${drawer.name}`;
        const quantityToAllocate = parseInt(itemForm.quantity) || 1;
        const issueNumber = generateIssueNumber();

        await axios.post(
          `${API_BASE_URL}/api/warehouse/warehouse-issues/`,
          {
            issue_number: issueNumber,
            issue_type: 'other',
            status: 'issued',
            assigned_to_person: allocationName,
            purpose: `Alokacja do szafki: ${allocationName}`,
            notes: `Nowy przedmiot dodany ręcznie do konfiguracji szafki`,
            items_data: [
              {
                item_id: warehouseItemId,
                quantity: quantityToAllocate,
                notes: `Szuflada: ${drawer.name}`
              }
            ]
          },
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
              'Content-Type': 'application/json'
            }
          }
        );
      }

      // Build drawer item
      const drawerItem = {
        ...createEmptyItem(),
        name: itemForm.name,
        description: `${categoryName}${itemForm.location ? ' • ' + itemForm.location : ''}`,
        quantity: parseInt(itemForm.quantity) || 1,
        warehouseItemId: warehouseItemId,
        unit: itemForm.unit,
        sku: itemForm.sku,
        image: itemFormImage,
      };

      if (isEditing) {
        // Update existing drawer item
        onUpdateDrawer({
          ...drawer,
          items: items.map((item, idx) =>
            idx === editingItemIndex ? drawerItem : item
          ),
        });
      } else {
        // Add new drawer item
        onUpdateDrawer({
          ...drawer,
          items: [...items, drawerItem],
        });
      }

      // Reset and close form
      setIsItemFormOpen(false);
      resetItemForm();
    } catch (error) {
      console.error('Error saving item:', error);
      alert('Błąd podczas zapisywania przedmiotu: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsSavingItem(false);
    }
  };

  // Cancel item form
  const handleCancelItemForm = () => {
    setIsItemFormOpen(false);
    resetItemForm();
  };

  // Delete item by index
  const handleDeleteItem = (index) => {
    onUpdateDrawer({
      ...drawer,
      items: items.filter((_, idx) => idx !== index),
    });
  };

  // Remove drawer image
  const handleRemoveDrawerImage = () => {
    onUpdateDrawer({ ...drawer, image: null });
  };

  // Close search modal
  const handleCloseSearch = () => {
    setIsSearchOpen(false);
    setSearchTerm('');
    setCategoryFilter('all');
    setLocationFilter('all');
  };

  return (
    <div className="qconfig-inventory">
      {/* Drawer Name & Image Section */}
      <div className="qconfig-inventory-header">
        <div className="qconfig-inventory-name-section">
          {isEditingDrawerName ? (
            <div className="qconfig-inline-edit">
              <input
                ref={drawerNameInputRef}
                type="text"
                value={editedDrawerName}
                onChange={(e) => setEditedDrawerName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveDrawerName();
                  if (e.key === 'Escape') setIsEditingDrawerName(false);
                }}
                className="qconfig-inline-input"
              />
              <button onClick={handleSaveDrawerName} className="qconfig-inline-btn save">
                <Check className="w-4 h-4" />
              </button>
              <button onClick={() => setIsEditingDrawerName(false)} className="qconfig-inline-btn cancel">
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="qconfig-inventory-name-display">
              <span>Drawer: {drawer.name}</span>
              <button onClick={handleStartEditDrawerName} className="qconfig-inline-edit-btn">
                <Edit3 className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>

        {/* Drawer Image Preview */}
        {drawer.image && (
          <div className="qconfig-drawer-image-preview">
            <img src={drawer.image} alt="Drawer layout" />
            <button onClick={handleRemoveDrawerImage} className="qconfig-remove-image-btn">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Items Section */}
      <div className="qconfig-items-section">
        <div className="qconfig-items-header">
          <h4>
            <Package className="w-4 h-4" />
            Items ({items.length})
          </h4>
          <div className="qconfig-items-header-actions">
            <button onClick={() => setIsSearchOpen(true)} className="qconfig-search-warehouse-btn">
              <Database className="w-4 h-4" />
              From Warehouse
            </button>
            <button onClick={handleAddItem} className="qconfig-add-item-btn">
              <Plus className="w-4 h-4" />
              Manual
            </button>
          </div>
        </div>

        {items.length === 0 ? (
          <div className="qconfig-no-items">
            <p>No items in this drawer</p>
            <div className="qconfig-no-items-actions">
              <button onClick={() => setIsSearchOpen(true)} className="qconfig-add-item-link primary">
                <Database className="w-4 h-4" />
                Add from Warehouse
              </button>
              <button onClick={handleAddItem} className="qconfig-add-item-link">
                + Add manually
              </button>
            </div>
          </div>
        ) : (
          <div className="qconfig-items-list">
            {items.map((item, index) => (
              <div key={index} className="qconfig-item-card">
                <div className="qconfig-item-view">
                  {item.image && (
                    <div className="qconfig-item-thumbnail">
                      <img src={item.image} alt={item.name} />
                    </div>
                  )}
                  <div className="qconfig-item-info">
                    <div className="qconfig-item-name-row">
                      <span className="qconfig-item-name">{item.name}</span>
                      <span className="qconfig-item-quantity">x{item.quantity}</span>
                    </div>
                    {item.description && (
                      <p className="qconfig-item-description">{item.description}</p>
                    )}
                    {item.warehouseItemId && (
                      <span className="qconfig-item-warehouse-badge">
                        <Database className="w-3 h-3" />
                        Warehouse Item
                      </span>
                    )}
                  </div>
                  <div className="qconfig-item-actions">
                    <button
                      onClick={() => handleEditItem(item, index)}
                      className="qconfig-item-action-btn edit"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteItem(index)}
                      className="qconfig-item-action-btn delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Warehouse Search Modal */}
      {isSearchOpen && (
        <div className="qconfig-warehouse-modal-overlay">
          <div className="qconfig-warehouse-modal">
            {/* Modal Header */}
            <div className="qconfig-warehouse-modal-header">
              <h3>
                <Database className="w-5 h-5" />
                Add from Warehouse
              </h3>
              <button onClick={handleCloseSearch} className="qconfig-warehouse-modal-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Search & Filters */}
            <div className="qconfig-warehouse-search-section">
              <div className="qconfig-warehouse-search-input-wrapper">
                <Search className="w-4 h-4" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search items by name, SKU, category..."
                  className="qconfig-warehouse-search-input"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="qconfig-warehouse-search-clear"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              <div className="qconfig-warehouse-filters">
                <div className="qconfig-warehouse-filter">
                  <Tag className="w-3 h-3" />
                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="qconfig-warehouse-filter-select"
                  >
                    <option value="all">All Categories</option>
                    {availableCategories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div className="qconfig-warehouse-filter">
                  <MapPin className="w-3 h-3" />
                  <select
                    value={locationFilter}
                    onChange={(e) => setLocationFilter(e.target.value)}
                    className="qconfig-warehouse-filter-select"
                  >
                    <option value="all">All Locations</option>
                    {availableLocations.map(loc => (
                      <option key={loc} value={loc}>{loc}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Items List */}
            <div className="qconfig-warehouse-items-list">
              {isLoadingWarehouse ? (
                <div className="qconfig-warehouse-loading">
                  <Loader2 className="w-8 h-8 animate-spin" />
                  <p>Loading warehouse items...</p>
                </div>
              ) : searchError ? (
                <div className="qconfig-warehouse-error">
                  <p>{searchError}</p>
                  <button onClick={fetchWarehouseItems} className="qconfig-warehouse-retry-btn">
                    Try Again
                  </button>
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="qconfig-warehouse-empty">
                  <Package className="w-12 h-12" />
                  <p>No items found</p>
                  {searchTerm && <span>Try different search terms</span>}
                </div>
              ) : (
                <>
                  <div className="qconfig-warehouse-items-count">
                    Showing {filteredItems.length} of {warehouseItems.length} items
                  </div>
                  {filteredItems.map(item => {
                    const isInDrawer = items.some(
                      i => i.warehouseItemId === item.id || i.name === item.name
                    );
                    return (
                      <div
                        key={item.id}
                        className={`qconfig-warehouse-item ${isInDrawer ? 'in-drawer' : ''}`}
                      >
                        <div className="qconfig-warehouse-item-info">
                          <div className="qconfig-warehouse-item-name">{item.name}</div>
                          <div className="qconfig-warehouse-item-meta">
                            {item.category && (
                              <span className="qconfig-warehouse-item-category">
                                <Tag className="w-3 h-3" />
                                {item.category}
                              </span>
                            )}
                            {item.location && (
                              <span className="qconfig-warehouse-item-location">
                                <MapPin className="w-3 h-3" />
                                {item.location}
                              </span>
                            )}
                            {item.sku && (
                              <span className="qconfig-warehouse-item-sku">
                                SKU: {item.sku}
                              </span>
                            )}
                          </div>
                          <div className="qconfig-warehouse-item-stock">
                            <span className={item.availableQty > 0 ? 'in-stock' : 'out-of-stock'}>
                              {item.availableQty > 0
                                ? `${item.availableQty} ${item.unit || 'pcs'} in stock`
                                : 'Out of stock'}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => handleAddWarehouseItem(item)}
                          className="qconfig-warehouse-item-add-btn"
                          disabled={item.availableQty === 0}
                        >
                          {isInDrawer ? (
                            <>
                              <Plus className="w-4 h-4" />
                              Add More
                            </>
                          ) : (
                            <>
                              <Plus className="w-4 h-4" />
                              Add
                            </>
                          )}
                        </button>
                      </div>
                    );
                  })}
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div className="qconfig-warehouse-modal-footer">
              <span className="qconfig-warehouse-added-count">
                {items.length} item{items.length !== 1 ? 's' : ''} in drawer
              </span>
              <button onClick={handleCloseSearch} className="qconfig-warehouse-done-btn">
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Item Modal */}
      {isItemFormOpen && (
        <div className="qconfig-warehouse-modal-overlay">
          <div className="qconfig-warehouse-modal qconfig-add-item-modal">
            <div className="qconfig-warehouse-modal-header">
              <h3>
                {editingItemIndex !== null ? <Edit3 className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
                {editingItemIndex !== null ? 'Edytuj przedmiot' : 'Dodaj nowy przedmiot'}
              </h3>
              <button onClick={handleCancelItemForm} className="qconfig-warehouse-modal-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {isLoadingItemData ? (
              <div className="qconfig-add-item-form" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
                <Loader2 className="w-8 h-8 animate-spin" />
                <span style={{ marginLeft: '0.5rem' }}>Ładowanie danych...</span>
              </div>
            ) : (
              <div className="qconfig-add-item-form">
                {/* Name */}
                <div className="qconfig-form-group">
                  <label>
                    <Package className="w-4 h-4" />
                    Nazwa *
                  </label>
                  <input
                    type="text"
                    value={itemForm.name}
                    onChange={(e) => handleItemFormChange('name', e.target.value)}
                    placeholder="Nazwa przedmiotu"
                    className="qconfig-form-input"
                    autoFocus
                  />
                </div>

                {/* Category & Subcategory */}
                <div className="qconfig-form-row">
                  <div className="qconfig-form-group">
                    <label>
                      <Layers className="w-4 h-4" />
                      Kategoria {editingItemIndex === null && '*'}
                    </label>
                    <select
                      value={itemForm.category}
                      onChange={(e) => handleItemFormChange('category', e.target.value)}
                      className="qconfig-form-select"
                    >
                      <option value="">Wybierz kategorię</option>
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="qconfig-form-group">
                    <label>
                      <Tag className="w-4 h-4" />
                      Podkategoria
                    </label>
                    <select
                      value={itemForm.subcategory}
                      onChange={(e) => handleItemFormChange('subcategory', e.target.value)}
                      className="qconfig-form-select"
                      disabled={!itemForm.category || subcategories.length === 0}
                    >
                      <option value="">Wybierz podkategorię</option>
                      {subcategories.map(sub => (
                        <option key={sub.id} value={sub.id}>{sub.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* SKU & Unit */}
                <div className="qconfig-form-row">
                  <div className="qconfig-form-group">
                    <label>
                      <Hash className="w-4 h-4" />
                      SKU
                    </label>
                    <input
                      type="text"
                      value={itemForm.sku}
                      onChange={(e) => handleItemFormChange('sku', e.target.value)}
                      placeholder="Kod SKU"
                      className="qconfig-form-input"
                    />
                  </div>
                  <div className="qconfig-form-group">
                    <label>Jednostka</label>
                    <select
                      value={itemForm.unit}
                      onChange={(e) => handleItemFormChange('unit', e.target.value)}
                      className="qconfig-form-select"
                    >
                      {UNIT_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Quantity & Price */}
                <div className="qconfig-form-row">
                  <div className="qconfig-form-group">
                    <label>Ilość</label>
                    <input
                      type="number"
                      value={itemForm.quantity}
                      onChange={(e) => handleItemFormChange('quantity', e.target.value)}
                      min="1"
                      className="qconfig-form-input"
                    />
                  </div>
                  <div className="qconfig-form-group">
                    <label>
                      <DollarSign className="w-4 h-4" />
                      Cena jednostkowa
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={itemForm.unit_price}
                      onChange={(e) => handleItemFormChange('unit_price', e.target.value)}
                      placeholder="0.00"
                      className="qconfig-form-input"
                    />
                  </div>
                </div>

                {/* Location */}
                <div className="qconfig-form-group qconfig-location-picker-wrapper">
                  <LocationPicker
                    selectedLocation={itemForm.location}
                    onLocationChange={(location) => handleItemFormChange('location', location)}
                  />
                </div>

                {/* Supplier */}
                <div className="qconfig-form-group">
                  <label>Dostawca</label>
                  <input
                    type="text"
                    value={itemForm.supplier}
                    onChange={(e) => handleItemFormChange('supplier', e.target.value)}
                    placeholder="Nazwa dostawcy"
                    className="qconfig-form-input"
                  />
                </div>

                {/* Description */}
                <div className="qconfig-form-group">
                  <label>
                    <FileText className="w-4 h-4" />
                    Opis
                  </label>
                  <textarea
                    value={itemForm.description}
                    onChange={(e) => handleItemFormChange('description', e.target.value)}
                    placeholder="Opis przedmiotu"
                    className="qconfig-form-textarea"
                    rows={2}
                  />
                </div>

                {/* Image */}
                <div className="qconfig-form-group">
                  <label>
                    <Camera className="w-4 h-4" />
                    Zdjęcie
                  </label>
                  <div className="qconfig-form-image-section">
                    {itemFormImage ? (
                      <div className="qconfig-form-image-preview">
                        <img src={itemFormImage} alt="Preview" />
                        <button
                          type="button"
                          onClick={() => setItemFormImage(null)}
                          className="qconfig-form-image-remove"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setShowItemFormCamera(true)}
                        className="qconfig-form-image-btn"
                      >
                        <Camera className="w-5 h-5" />
                        <span>Dodaj zdjęcie</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="qconfig-warehouse-modal-footer">
              <button onClick={handleCancelItemForm} className="qconfig-btn-secondary">
                Anuluj
              </button>
              <button
                onClick={handleSaveItem}
                className="qconfig-btn-primary"
                disabled={isSavingItem || isLoadingItemData || !itemForm.name.trim() || (editingItemIndex === null && !itemForm.category)}
              >
                {isSavingItem ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Zapisywanie...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    {editingItemIndex !== null ? 'Zapisz zmiany' : 'Zapisz przedmiot'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Camera for item form */}
      <CameraCapture
        isOpen={showItemFormCamera}
        onClose={() => setShowItemFormCamera(false)}
        onCapture={(imageData) => {
          setItemFormImage(imageData);
          setShowItemFormCamera(false);
        }}
        title="Zdjęcie przedmiotu"
      />
    </div>
  );
};

export default QConfiguratorDrawerInventory;
